import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Box, Chip, Stack } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import MasterTypeChip from 'components/chip/MasterTypeChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleHeaderAction,
  ConsoleSearchFilter,
  ConsoleTable,
  useQueryInitialFilters,
} from 'components/console';
import {
  CsvBulkUploadDialog,
  CsvTemplateDownloadDialog,
  downloadCsvTemplate,
} from 'components/csv';
import { CsvBulkConfig } from 'components/csv/types';
import { MasterTypeResult, getPartnerType } from 'stores/MasterTypeStoreAdapter';
import { CreatePartnerParams, PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import PartnerCreateForm, { PartnerCreateFormHandle } from './components/PartnerCreateForm';
import PartnerUpdateForm, { PartnerUpdateFormHandle } from './components/PartnerUpdateForm';
import { createPartnerBulkConfig, createPartnerCsvFields } from './components/partnerCsvConfig';

type DrawerMode = 'none' | 'create' | 'detail';

const PartnerManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [isProgress, setProgress] = useState(true);
  const [partners, setPartners] = useState<PartnerResult[]>([]);
  const [partnerTypes, setPartnerTypes] = useState<MasterTypeResult[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<PartnerResult | null>(null);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('none');
  const [isEditingPartner, setIsEditingPartner] = useState(false);
  const [isCsvTemplateDialogOpen, setIsCsvTemplateDialogOpen] = useState(false);
  const [isCsvBulkDialogOpen, setIsCsvBulkDialogOpen] = useState(false);
  // 現在適用中の検索パラメータ（作成/更新/CSV後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<Record<string, string>>({});

  const createFormRef = useRef<PartnerCreateFormHandle>(null);
  const updateFormRef = useRef<PartnerUpdateFormHandle>(null);

  useEffect(() => {
    getPartnerType()
      .then(setPartnerTypes)
      .catch(() => {});
  }, []);

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchPartners(activeParams);

  const fetchPartners = (params: Record<string, string>) => {
    setProgress(true);
    listPartner(params)
      .then((result: PartnerResult[]) => {
        setPartners(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('パートナー情報の取得に失敗しました');
      });
  };

  const closeDrawer = () => {
    setDrawerMode('none');
    setSelectedPartner(null);
    setIsEditingPartner(false);
  };

  // 作成・更新後は共通で「フォームを閉じて現在の検索条件で再検索」
  const handleCreated = () => {
    closeDrawer();
    refetch();
  };

  const handlePartnerUpdated = () => {
    closeDrawer();
    refetch();
  };

  const handleSearch = (filters: Filter[]) => {
    closeDrawer();
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchPartners(params);
  };

  const handleCreateClick = () => {
    setSelectedPartner(null);
    setIsEditingPartner(false);
    setDrawerMode('create');
  };

  const handleRowClick = (row: PartnerResult) => {
    setIsEditingPartner(false);
    setSelectedPartner(row);
    setDrawerMode('detail');
  };

  const typeIndexOf = (partnerTypeId: string) =>
    partnerTypes.findIndex((t) => t.typeCode === partnerTypeId);

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'partnerName', name: 'パートナー名', type: 'text', color: '#78C1F3' },
      {
        key: 'partnerNameKana',
        name: 'パートナー名（カナ）',
        type: 'katakana-text',
        color: '#3A98B9',
      },
      {
        key: 'partnerTypeId',
        name: 'パートナー種別',
        type: 'select',
        color: '#B799FF',
        values: partnerTypes.map((t) => ({ key: t.typeCode, name: t.typeLabel })),
      },
      {
        key: 'includeExpired',
        name: '契約終了を含む',
        type: 'check',
        color: '#78909C',
        value: { key: 'true', name: '契約終了を含む' },
      },
    ],
    [partnerTypes],
  );

  // URL クエリパラメータを初期検索条件として反映
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions);
  useEffect(() => {
    setActiveParams(queryParams);
    fetchPartners(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const csvFields = useMemo(() => createPartnerCsvFields(partnerTypes), [partnerTypes]);
  const csvBulkConfig: CsvBulkConfig<CreatePartnerParams> | null = useMemo(
    () => (partnerTypes.length > 0 ? createPartnerBulkConfig(partnerTypes) : null),
    [partnerTypes],
  );
  const handleCsvTemplateDownload = () => {
    downloadCsvTemplate(csvFields, 'パートナー一括登録テンプレート.csv');
  };

  const menuActions: ConsoleHeaderAction[] = [
    {
      label: 'CSV一括登録',
      icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvBulkDialogOpen(true),
    },
    {
      label: 'CSVテンプレートをダウンロード',
      icon: <FileDownloadIcon sx={{ fontSize: 18 }} />,
      onClick: () => setIsCsvTemplateDialogOpen(true),
    },
  ];

  const columns: ConsoleColumn<PartnerResult>[] = [
    {
      key: 'partnerName',
      header: 'パートナー名',
      width: 240,
      ellipsis: (p) => p.partnerName,
    },
    {
      key: 'kana',
      header: 'カナ',
      width: 180,
      ellipsis: (p) => p.partnerNameKana,
    },
    {
      key: 'type',
      header: '種別',
      width: 100,
      render: (p) => {
        const idx = typeIndexOf(p.partnerTypeId);
        return idx >= 0 ? (
          <MasterTypeChip label={partnerTypes[idx].typeLabel} colorIndex={idx} size="small" />
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        );
      },
    },
    {
      key: 'contactName',
      header: '担当者',
      width: 140,
      ellipsis: (p) => p.contactName ?? '',
    },
    {
      key: 'status',
      header: '状態',
      width: 90,
      render: (p) => {
        const active = !p.contractEndDate || new Date(p.contractEndDate) >= new Date();
        return (
          <Chip
            label={active ? '契約中' : '契約終了'}
            size="small"
            color={active ? 'primary' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      key: 'period',
      header: '契約期間',
      width: 170,
      render: (p) => (
        <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {p.contractStartDate ? formatDate(p.contractStartDate) : '—'}
          {p.contractEndDate ? ` 〜 ${formatDate(p.contractEndDate)}` : ''}
        </Box>
      ),
    },
  ];

  const selectedActive =
    selectedPartner &&
    (!selectedPartner.contractEndDate || new Date(selectedPartner.contractEndDate) >= new Date());

  const drawerFooter =
    drawerMode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton label="キャンセル" buttonVariant="secondary" onClick={closeDrawer} />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<AddBusinessIcon />}
          onClick={() => createFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedPartner && isEditingPartner ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setIsEditingPartner(false)}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={() => updateFormRef.current?.submit()}
        />
      </Stack>
    ) : selectedPartner ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={() => setIsEditingPartner(true)}
        />
      </Stack>
    ) : undefined;

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="パートナー"
        count={partners.length}
        description="協力会社・個人事業主の登録・更新・検索を行います"
        backLabel="要員管理"
        onBack={() => navigate('/member')}
        onRefresh={refetch}
        menuActions={menuActions}
        actions={
          <CustomButton
            label="パートナーを登録"
            buttonVariant="primary"
            icon={<AddBusinessIcon />}
            onClick={handleCreateClick}
          />
        }
      />

      <ConsoleTable
        columns={columns}
        rows={partners}
        rowKey={(p) => p.partnerId}
        selectedKey={drawerMode === 'detail' ? selectedPartner?.partnerId : undefined}
        onRowClick={handleRowClick}
        emptyMessage="パートナーがありません。「パートナーを登録」または「CSV一括登録」から登録してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="partner-list"
            userProfileKey="member-partner-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={drawerMode === 'create'}
        onClose={closeDrawer}
        title="パートナーの登録"
        width={640}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <PartnerCreateForm
              ref={createFormRef}
              onCreate={handleCreated}
              onCancel={closeDrawer}
              setProgress={setProgress}
              hideHeader
            />
          </Box>
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
        </Box>
      </ConsoleDetailDrawer>

      <ConsoleDetailDrawer
        open={drawerMode === 'detail' && selectedPartner !== null}
        onClose={closeDrawer}
        title={selectedPartner?.partnerName ?? ''}
        badges={
          selectedPartner && (
            <Chip
              label={selectedActive ? '契約中' : '契約終了'}
              size="small"
              color={selectedActive ? 'primary' : 'default'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )
        }
        width={640}
      >
        {selectedPartner && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <PartnerUpdateForm
                ref={updateFormRef}
                partner={selectedPartner}
                isEditing={isEditingPartner}
                onPartnerUpdated={handlePartnerUpdated}
                onClose={closeDrawer}
                onEditingChange={setIsEditingPartner}
                setProgress={setProgress}
                hideHeader
              />
            </Box>
            {drawerFooter && (
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', mt: 2 }}>{drawerFooter}</Box>
            )}
          </Box>
        )}
      </ConsoleDetailDrawer>

      <CsvTemplateDownloadDialog
        open={isCsvTemplateDialogOpen}
        onClose={() => setIsCsvTemplateDialogOpen(false)}
        entityName="パートナー"
        fields={csvFields}
        onDownload={handleCsvTemplateDownload}
      />

      {csvBulkConfig && (
        <CsvBulkUploadDialog
          open={isCsvBulkDialogOpen}
          onClose={() => setIsCsvBulkDialogOpen(false)}
          config={csvBulkConfig}
          onComplete={() => {
            closeDrawer();
            refetch();
          }}
        />
      )}

      <Circular open={isProgress} />
    </Box>
  );
};

export default PartnerManagementPage;
